import { useEffect } from "react";

import ScrollTop from "../features/ScrollTop";

function MainTermsConditions() {
  useEffect(() => {
    document.title = "Terms And Conditions | AttnQ";
  }, []);

  return (
    <main className="text-gray-800 px-8 py-20 shadow">
      <ScrollTop />
      <div className="max-w-5xl mx-auto space-y-10">
        {/* Page Heading */}
        <header className="text-center space-y-2">
          <h1 className="text-xl md:text-3xl font-bold">
            Terms&nbsp;&amp;&nbsp;Conditions
          </h1>
          <p className="text-sm text-gray-500">
            Updated on and effective from:{" "}
            <span className="italic">[04&nbsp;06&nbsp;2024]</span>
          </p>
        </header>

        {/* Terms body */}
        <article className="space-y-8">
          {/* 1 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              1. Acceptance of Terms
            </h2>
            <p className="text-sm text-gray-600">
              By accessing or using the AttnQ platform (“
              <strong>Platform</strong>”), you agree to be bound by these Terms
              and Conditions (“<strong>Terms</strong>”). If you do not accept
              the Terms, you must not use the Platform.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              2. Definitions
            </h2>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              <li>
                <strong>“Buyer” / "Investor"</strong> – any user submitting
                property requirements.
              </li>
              <li>
                <strong>“Broker” / “Agent”</strong> – any user accessing those
                requirements to offer properties.
              </li>
              <li>
                <strong>“Lead”</strong> – a buyer/investor requirement shared
                with a broker.
              </li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              3. Eligibility &amp; Accounts
            </h2>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
              <li>You must be at least 18&nbsp;years old.</li>
              <li>Provide accurate, complete registration data.</li>
              <li>Maintain the confidentiality of your login credentials.</li>
              <li>
                Notify AttnQ immediately of any unauthorised use of your
                account.
              </li>
            </ol>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              4. Platform Use &amp; Licence
            </h2>
            <p className="text-sm text-gray-600">
              AttnQ grants you a limited, non-exclusive, non-transferable, and
              revocable license to access and use the Platform strictly in
              accordance with these Terms and Conditions. This license is
              intended solely to enable you to evaluate, submit, or respond to
              property requirements as a buyer or broker through AttnQ's
              matchmaking features. You may use the Platform only for your
              personal or professional real estate purposes, and not for any
              commercial resale, redistribution, or mass extraction of data.
              <br />
              <br />
              You are not permitted to copy, modify, distribute,
              reverse-engineer, or create derivative works from any part of the
              Platform, including its design, structure, codebase, or user data.
              Automated scripts, bots, or unauthorized software to access the
              Platform are strictly prohibited. The Platform and its content
              remain the sole intellectual property of AttnQ, and your use of
              the service does not grant you ownership of any part of it.
              <br />
              <br />
              AttnQ reserves the right to suspend or revoke your access at any
              time, without prior notice, if it determines that your use
              violates these Terms, applicable law, or harms the integrity of
              the platform or its users.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              5. Buyer Obligations
            </h2>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              <li>Provide truthful property requirements.</li>
              <li>Respond courteously to broker outreach.</li>
              <li>Avoid posting fraudulent or misleading information.</li>
              <li>Comply with all applicable real-estate regulations.</li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              6. Broker Obligations
            </h2>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              <li>
                Hold valid licences/registrations required by law (e.g., RERA).
              </li>
              <li>
                Contact buyers only via consented channels and within rate
                limits.
              </li>
              <li>Provide accurate property details; no misrepresentation.</li>
              <li>
                Pay all fees and commissions due to AttnQ in a timely manner.
              </li>
            </ul>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              7. Fees, Payments &amp; Refunds
            </h2>
            <p className="text-sm text-gray-600">
              AttnQ operates on a hybrid pricing model designed to keep the
              platform accessible while rewarding performance. Brokers can
              purchase lead credits in prepaid bundles or activate a recurring
              subscription that unlocks a set number of qualified buyer profiles
              each month. All fees are denominated in Indian Rupees (INR) unless
              otherwise stated and are exclusive of applicable taxes, which will
              be added at checkout. Payment is processed through our integrated
              gateway partners, and by completing a transaction you authorize
              AttnQ to charge the selected payment method for the agreed amount,
              including any recurring charges tied to a subscription plan.
              <br />
              <br />
              Buyers are not charged for posting requirements; however, when a
              successful match results in a finalized property transaction, a
              success-based commission may be invoiced. This commission is
              always disclosed in advance and must be settled within the payment
              window specified on the invoice. Failure to pay may lead to
              suspension of platform privileges for both parties involved and
              may trigger additional collection procedures.
              <br />
              <br />
              Except where mandatory consumer-protection law requires otherwise,
              all purchases of lead credits, subscription fees, and success
              commissions are non-refundable. Refunds are considered only in
              cases of duplicate billing or demonstrable technical errors that
              prevent delivery of the paid service. Any approved refund will be
              issued to the original payment method within fifteen business days
              and will exclude third-party transaction charges that AttnQ cannot
              recover.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              8. Intellectual Property
            </h2>
            <p className="text-sm text-gray-600">
              All content, features, and functionality available on the AttnQ
              Platform—including but not limited to the website design,
              codebase, branding, logos, matching algorithms, written copy, and
              user interface elements—are the exclusive property of AttnQ or its
              licensors. These elements are protected under applicable
              intellectual property laws, including copyright, trademark, and
              trade secret regulations. No part of the Platform may be copied,
              reproduced, republished, uploaded, or distributed in any form
              without prior written consent from AttnQ.
              <br />
              <br />
              By using the Platform, you do not acquire any ownership rights or
              licenses to AttnQ’s intellectual property. You may not use AttnQ’s
              name, logo, or any content from the Platform in a way that implies
              partnership, endorsement, or association without express written
              approval.
              <br />
              <br />
              However, when you submit content to the Platform—such as property
              requirements, listings, or profile details—you retain ownership of
              that content. You grant AttnQ a non-exclusive, royalty-free,
              global license to store, process, display, and share your
              submitted content solely for the purposes of enabling property
              matchmaking, fulfilling service functionality, and improving the
              Platform.
              <br />
              <br />
              Any unauthorized use or reproduction of AttnQ’s intellectual
              property may result in legal action, including suspension of
              account access and pursuit of damages where applicable.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              9. Prohibited Activities
            </h2>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              <li>Data scraping or automated collection of leads.</li>
              <li>Sending spam or unsolicited messages.</li>
              <li>Posting illegal, defamatory, or obscene content.</li>
              <li>Engaging in fraudulent or deceptive practices.</li>
            </ul>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              10. Disclaimer of Warranties
            </h2>
            <p className="text-sm text-gray-600">
              The AttnQ Platform is provided on an “as is” and “as available”
              basis, without any express or implied warranties of any kind.
              While we strive to offer a seamless and reliable service, AttnQ
              makes no guarantees regarding the accuracy, completeness, or
              reliability of any content, listings, buyer or broker information,
              or property matches available through the Platform.
              <br />
              <br />
              We do not warrant that the Platform will be error-free,
              uninterrupted, or immune to bugs, delays, or technical issues. You
              acknowledge that occasional downtime, system updates, and
              third-party service interruptions may occur, and that these are
              not grounds for compensation or refunds unless otherwise
              specified.
              <br />
              <br />
              AttnQ does not endorse, verify, or guarantee the legitimacy of any
              user, broker, property, or deal listed on the Platform. All
              decisions made based on information available on AttnQ—such as
              contacting a broker or proceeding with a transaction—are made at
              your own discretion and risk.
              <br />
              <br />
              To the fullest extent permitted by law, AttnQ disclaims all
              warranties, whether statutory, express, or implied, including but
              not limited to warranties of merchantability, fitness for a
              particular purpose, and non-infringement. You assume full
              responsibility for the outcomes of using the Platform and are
              encouraged to conduct independent due diligence before entering
              into any financial or real estate agreement.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              11. Limitation of Liability
            </h2>
            <p className="text-sm text-gray-600">
              To the maximum extent permitted by applicable law, AttnQ shall not
              be held liable for any direct, indirect, incidental,
              consequential, special, or exemplary damages arising out of or in
              connection with your access to, use of, or inability to use the
              Platform. This includes—but is not limited to—loss of profits,
              goodwill, property deals, data, business opportunities, or
              reputational harm, even if AttnQ has been advised of the
              possibility of such damages.
              <br />
              <br />
              AttnQ acts solely as a technology intermediary between property
              buyers and brokers. We do not participate in or control any
              offline interactions, negotiations, or transactions between users.
              As a result, we cannot and do not guarantee that a match will lead
              to a successful property transaction, nor can we be held
              responsible for any disputes, misrepresentations, or contractual
              failures that may arise between parties.
              <br />
              <br />
              Our total aggregate liability, in any circumstance and regardless
              of the cause of action, shall not exceed the greater of (a) the
              amount paid by you (if any) to AttnQ in the past six months for
              use of the Platform, or (b) ₹5,000 (INR five thousand only).
              <br />
              <br />
              Some jurisdictions may not allow certain limitations on liability,
              so portions of this clause may not apply to you. In all cases,
              AttnQ’s liability will be limited to the fullest extent allowed by
              law.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              12. Indemnification
            </h2>
            <p className="text-sm text-gray-600">
              You agree to indemnify, defend, and hold harmless AttnQ, its
              parent company, directors, officers, employees, affiliates, and
              authorized service providers from and against any and all claims,
              demands, liabilities, losses, damages, penalties, fines, expenses,
              and legal fees (including reasonable attorneys’ fees) arising out
              of or related to your violation of these Terms and Conditions;
              your use or misuse of the Platform; your interactions or disputes
              with other users, including but not limited to property buyers,
              brokers, or third-party vendors; any content or information you
              submit, upload, or transmit through the Platform; any breach of
              applicable laws, regulations, or third-party rights (including
              intellectual property or data protection laws) committed during
              your use of the Platform.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              13. Termination &amp; Suspension
            </h2>
            <p className="text-sm text-gray-600">
              AttnQ may suspend or terminate your account at any time for
              violation of these Terms, with or without notice. Upon
              termination, your right to use the Platform ceases immediately.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-xl md:text-2xl font-semibold my-2">
              14. Changes to Terms
            </h2>
            <p className="text-sm text-gray-600">
              We may update this Terms and Conditions from time to time to
              reflect changes in our services, legal requirements, or internal
              practices, and we encourage you to review it periodically.
              Whenever a material revision is made, we will post the new version
              on our website and, where feasible, provide additional notice—such
              as sending an email or displaying an in-app alert—so that you
              remain informed about how your data is handled. Because our
              product evolves quickly, we reserve the right to amend or replace
              the policy without prior notice when immediate changes are
              necessary to protect user data, comply with urgent legal
              directives, or address emerging security concerns. Your continued
              use of AttnQ after any modification constitutes acceptance of the
              revised terms; if you do not agree, you should discontinue use of
              the platform.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}

export default MainTermsConditions;
